import type { QueryClient } from '@tanstack/react-query';
import type { CorSelo } from '../../componentes/base';

export type Origem = 'app' | 'web' | 'ajuste' | 'relogio' | 'importacao';
export const ORIGENS: Origem[] = ['app', 'web', 'ajuste', 'relogio', 'importacao'];
export const COR_ORIGEM: Record<Origem, CorSelo> = { app: 'azul', web: 'ciano', ajuste: 'laranja', relogio: 'roxo', importacao: 'neutro' };

export type EstadoAjuste = 'pedido' | 'aprovado' | 'recusado' | 'cancelado';
export const ESTADOS_AJUSTE: EstadoAjuste[] = ['pedido', 'aprovado', 'recusado', 'cancelado'];
export const COR_AJUSTE: Record<EstadoAjuste, CorSelo> = { pedido: 'laranja', aprovado: 'verde', recusado: 'perigo', cancelado: 'neutro' };

export type Situacao = 'normal' | 'falta' | 'abonado' | 'descanso' | 'incompleto' | 'hoje' | 'futuro' | 'fora_do_vinculo';
export const COR_SITUACAO: Record<Situacao, CorSelo> = {
  normal: 'verde',
  falta: 'perigo',
  abonado: 'azul',
  descanso: 'neutro',
  incompleto: 'laranja',
  hoje: 'ciano',
  futuro: 'neutro',
  fora_do_vinculo: 'neutro',
};

export type Regime = 'nenhum' | 'individual' | 'coletivo';
export const REGIMES: Regime[] = ['nenhum', 'individual', 'coletivo'];
export type Contrato = 'clt' | 'pj' | 'estagio' | 'temporario';
export const CONTRATOS: Contrato[] = ['clt', 'pj', 'estagio', 'temporario'];
export type Escopo = 'empresa' | 'departamento' | 'funcionario';
export const ESCOPOS: Escopo[] = ['empresa', 'departamento', 'funcionario'];

export interface Horario {
  entrada: string;
  intervalo_inicio: string | null;
  intervalo_fim: string | null;
  saida: string;
}

export interface Cerca {
  latitude: number;
  longitude: number;
  raio_m: number;
}

export interface Hoje {
  agora: string;
  hoje: string;
  funcionario: { id: string; nome: string } | null;
  bate_ponto: boolean;
  empresa_id: string | null;
  marcacoes: { id: string; hora: string; origem: Origem; fora_de_linha: boolean }[];
  proxima: 'entrada' | 'saida';
  horario: Horario | null;
  esperado_min: number;
  trabalhado_min: number;
  politica: { exige_localizacao: boolean; exige_foto: boolean; cerca: Cerca | null } | null;
}

export interface Marcacao {
  id: string;
  funcionario_id: string;
  funcionario_nome: string | null;
  empresa_id: string;
  momento: string;
  data_referencia: string;
  hora: string;
  recebida_em: string;
  hora_aparelho: string | null;
  fora_de_linha: boolean;
  origem: Origem;
  latitude: number | null;
  longitude: number | null;
  precisao_m: number | null;
  dentro_da_cerca: boolean | null;
  foto_anexo_id: string | null;
  ip: string | null;
  ajuste_id: string | null;
  desconsiderada: boolean;
  ajuste_pendente: boolean;
}

export interface Ajuste {
  id: string;
  funcionario_id: string;
  funcionario_nome: string | null;
  tipo: 'incluir' | 'desconsiderar';
  momento: string | null;
  hora: string | null;
  marcacao_id: string | null;
  data_referencia: string;
  justificativa: string;
  estado: EstadoAjuste;
  pedido_por_nome: string | null;
  motivo_decisao: string | null;
  decidido_por_nome: string | null;
  decidido_em: string | null;
  created_at: string;
  pode_decidir?: boolean;
  pode_cancelar?: boolean;
}

export interface DiaEspelho {
  data: string;
  dia_semana: string;
  situacao: Situacao;
  marcacoes: string[];
  horario: Horario | null;
  esperado_min: number;
  trabalhado_min: number;
  intervalo_min: number | null;
  intervalo_curto: boolean;
  atraso_min: number;
  saida_antecipada_min: number;
  extra50_min: number;
  extra100_min: number;
  debito_min: number;
  falta_min: number;
  folga_banco_min: number;
  tolerancia_aplicada: boolean;
  ausencia: string | null;
}

export interface Espelho {
  funcionario_id: string;
  funcionario_nome: string;
  de: string;
  ate: string;
  regime: Regime;
  tolerancia_marcacao_min: number;
  tolerancia_dia_min: number;
  ausencias_ligado: boolean;
  dias: DiaEspelho[];
  totais: { esperado_min: number; trabalhado_min: number; extra50_min: number; extra100_min: number; debito_min: number; falta_min: number; faltas_dias: number; abonados_dias: number; atrasos_dias: number; incompletos_dias: number };
  resultado: { regime: Regime; banco_credito_min: number; banco_debito_min: number; he50_min: number; he100_min: number; desconto_min: number };
  ajustes_pendentes: number;
}

export interface SaldoBanco {
  id: string;
  funcionario_id: string;
  funcionario_nome: string | null;
  empresa_id: string;
  empresa_nome: string | null;
  saldo_min: number;
  a_vencer_30_dias_min: number;
  proximo_vencimento: string | null;
  ate_mes: string | null;
}

export interface LancamentoBanco {
  id: string;
  data: string;
  minutos: number;
  natureza: 'credito' | 'debito' | 'vencimento';
  regime: 'individual' | 'coletivo' | null;
  vencimento: string | null;
  ano_mes: string;
}

export interface Fechamento {
  id: string;
  empresa_id: string;
  empresa_nome: string | null;
  ano_mes: string;
  estado: 'fechado' | 'reaberto';
  funcionarios: number;
  fechado_por_nome: string | null;
  fechado_em: string;
  reaberto_por_nome: string | null;
  reaberto_em: string | null;
  motivo_reabertura: string | null;
  folha_he50_min: number;
  folha_he100_min: number;
  folha_desconto_min: number;
}

export interface ItemFechamento {
  funcionario_id: string;
  funcionario_nome: string | null;
  regime: Regime;
  esperado_min: number;
  trabalhado_min: number;
  extra50_min: number;
  extra100_min: number;
  debito_min: number;
  falta_min: number;
  faltas_dias: number;
  abonados_dias: number;
  banco_credito_min: number;
  banco_debito_min: number;
  banco_vencido_pagar_min: number;
  banco_vencido_descontar_min: number;
  banco_saldo_min: number;
  folha_he50_min: number;
  folha_he100_min: number;
  folha_desconto_min: number;
}

export interface Configuracao {
  tolerancia_marcacao_min: number;
  tolerancia_dia_min: number;
  contratos: Contrato[];
  banco_regime: Regime;
  exige_localizacao: boolean;
  exige_foto: boolean;
}

export interface Politica {
  id: string;
  escopo: Escopo;
  alvo_id: string;
  alvo_nome: string | null;
  exige_localizacao: boolean | null;
  exige_foto: boolean | null;
  cerca_ativa: boolean | null;
  cerca_latitude: number | null;
  cerca_longitude: number | null;
  cerca_raio_m: number | null;
  banco_regime: Regime | null;
}

/** Minutos como "8h05" (com sinal quando pedido). */
export function horas(min: number | null | undefined, comSinal = false): string {
  if (min === null || min === undefined) return '-';
  const sinal = min < 0 ? '-' : comSinal && min > 0 ? '+' : '';
  const a = Math.abs(Math.round(min));
  return `${sinal}${Math.floor(a / 60)}h${String(a % 60).padStart(2, '0')}`;
}

/** Os últimos `n` meses (AAAA-MM), do atual para trás. */
export function ultimosMeses(hoje: string, n: number, incluirAtual = true): string[] {
  const [a, m] = [Number(hoje.slice(0, 4)), Number(hoje.slice(5, 7))];
  const saida: string[] = [];
  for (let i = incluirAtual ? 0 : 1; saida.length < n; i++) {
    const d = new Date(Date.UTC(a, m - 1 - i, 1));
    saida.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return saida;
}

export const invalidarPonto = (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['ponto'] });

// Lote 3 (relógio de ponto).
export type Marca = 'controlid' | 'henry' | 'dimep' | 'madis' | 'topdata' | 'secullum' | 'outra';
export const MARCAS: Marca[] = ['controlid', 'henry', 'dimep', 'madis', 'topdata', 'secullum', 'outra'];

export interface Relogio {
  id: string;
  nome: string;
  marca: Marca;
  modelo: string | null;
  numero_serie: string;
  empresa_id: string;
  empresa_nome: string | null;
  local: string | null;
  fuso: string;
  conferir_crc: boolean;
  ativo: boolean;
  leitura_automatica: boolean;
  endereco: string | null;
  certificado_sha256: string | null;
  ultimo_nsr: number | null;
  ultima_leitura_em: string | null;
  ultimo_erro: string | null;
  ultimo_erro_codigo: string | null;
  marcacoes: number;
}

export type EstadoImportacao = 'na_fila' | 'processando' | 'concluida' | 'erro';
export const COR_IMPORTACAO: Record<EstadoImportacao, CorSelo> = { na_fila: 'neutro', processando: 'ciano', concluida: 'verde', erro: 'perigo' };

export interface Importacao {
  id: string;
  relogio_id: string;
  relogio_nome: string | null;
  origem: 'arquivo' | 'api';
  anexo_id: string;
  nome_arquivo: string | null;
  estado: EstadoImportacao;
  leiaute: '1510' | '671' | null;
  periodo_inicio: string | null;
  periodo_fim: string | null;
  nsr_inicial: number | null;
  nsr_final: number | null;
  lidas: number;
  processadas: number;
  gravadas: number;
  existentes: number;
  sem_funcionario: number;
  periodo_fechado: number;
  crc_invalido: number;
  invalidas: number;
  outros_registros: number;
  fora_de_ordem: number;
  lacunas: number;
  erro: string | null;
  erro_codigo: string | null;
  pedido_por_nome: string | null;
  created_at: string;
  concluida_em: string | null;
  recusas?: { linha: number; nsr: number | null; codigo: string; motivo: string }[];
}

// Lote 4 (ponto oficial REP-P e arquivos fiscais).
export interface ConfigRepP {
  empresa_id: string;
  ativo: boolean;
  inpi: string | null;
  local_prestacao: string | null;
  caepf_cno: string | null;
  desenvolvedor_documento: string | null;
  desenvolvedor_razao: string | null;
  desenvolvedor_email: string | null;
  ultimo_nsr: number;
  ativado_em: string | null;
  certificado_configurado: boolean;
  oficial: boolean;
}

export interface ArquivoFiscal {
  id: string;
  empresa_id: string;
  empresa_nome: string | null;
  tipo: 'afd' | 'aej';
  de: string;
  ate: string;
  anexo_id: string;
  assinatura_anexo_id: string | null;
  nome_arquivo: string;
  registros: number;
  oficial: boolean;
  sha256: string;
  gerado_por_nome: string | null;
  created_at: string;
}
